import { useTranslation } from 'react-i18next';
import { Ban, XCircle, X, Minus, Check, Star } from 'lucide-react';
import type { ScoreValue } from '@/types/evaluation.types';
import { cn } from '@/lib/utils';

interface ScoreRadioGroupProps {
  value: ScoreValue;
  onChange: (score: ScoreValue) => void;
  criterionId: string;
}

const SCORE_OPTIONS = [
  {
    value: 0 as const,
    label: 'scorecard:scores.skip',
    shortLabel: 'Skip',
    icon: Ban,
    selectedColor: 'bg-muted/50 border-muted-foreground/30',
    unselectedColor: 'hover:bg-muted/30',
    textColor: 'text-muted-foreground',
  },
  {
    value: 1 as const,
    label: 'scorecard:scores.unacceptable',
    shortLabel: '1',
    icon: XCircle,
    selectedColor: 'bg-red-100 border-red-600 dark:bg-red-950/40 dark:border-red-700',
    unselectedColor: 'hover:bg-red-50 dark:hover:bg-red-950/20',
    textColor: 'text-red-700 dark:text-red-600',
  },
  {
    value: 2 as const,
    label: 'scorecard:scores.doesNotMeet',
    shortLabel: '2',
    icon: X,
    selectedColor: 'bg-red-100 border-red-500 dark:bg-red-950/30 dark:border-red-700',
    unselectedColor: 'hover:bg-red-50 dark:hover:bg-red-950/20',
    textColor: 'text-red-700 dark:text-red-500',
  },
  {
    value: 3 as const,
    label: 'scorecard:scores.partiallyMeets',
    shortLabel: '3',
    icon: Minus,
    selectedColor: 'bg-orange-100 border-orange-500 dark:bg-orange-950/30 dark:border-orange-700',
    unselectedColor: 'hover:bg-orange-50 dark:hover:bg-orange-950/20',
    textColor: 'text-orange-700 dark:text-orange-500',
  },
  {
    value: 4 as const,
    label: 'scorecard:scores.meetsExpectations',
    shortLabel: '4',
    icon: Check,
    selectedColor: 'bg-green-100 border-green-500 dark:bg-green-950/30 dark:border-green-700',
    unselectedColor: 'hover:bg-green-50 dark:hover:bg-green-950/20',
    textColor: 'text-green-700 dark:text-green-500',
  },
  {
    value: 5 as const,
    label: 'scorecard:scores.exceedsExpectations',
    shortLabel: '5',
    icon: Star,
    selectedColor: 'bg-emerald-100 border-emerald-500 dark:bg-emerald-950/30 dark:border-emerald-700',
    unselectedColor: 'hover:bg-emerald-50 dark:hover:bg-emerald-950/20',
    textColor: 'text-emerald-700 dark:text-emerald-500',
  },
] as const;

/**
 * Radio group for selecting criterion scores
 * Supports keyboard navigation (0-5 keys, Tab, Arrow keys)
 */
export function ScoreRadioGroup({ value, onChange, criterionId }: ScoreRadioGroupProps) {
  const { t } = useTranslation('scorecard');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle 0-5 key presses
    if (e.key >= '0' && e.key <= '5') {
      e.preventDefault();
      const score = parseInt(e.key) as ScoreValue;
      onChange(score);
    }
    // Handle Backspace/Delete to clear score
    else if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      onChange(null);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-labelledby={`score-label-${criterionId}`}
      className="flex flex-wrap items-center gap-1"
      onKeyDown={handleKeyDown}
    >
      {SCORE_OPTIONS.map((option) => {
        const isSelected = value === option.value;
        const Icon = option.icon;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={t(option.label)}
            title={t(option.label)}
            onClick={() => onChange(option.value)}
            className={cn(
              'relative flex flex-col items-center justify-center gap-0.5',
              'w-10 h-10 sm:w-12 sm:h-12 rounded-lg',
              'border-2 transition-all',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              option.textColor,
              isSelected
                ? `${option.selectedColor} shadow-sm`
                : `border-border bg-background ${option.unselectedColor}`
            )}
          >
            <Icon className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
            <span className="text-[9px] sm:text-[10px] font-medium">{option.shortLabel}</span>
          </button>
        );
      })}

      {/* Clear button when a score is selected */}
      {value !== null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-2 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 border-dashed border-muted-foreground/30 bg-background hover:bg-muted/30 hover:border-muted-foreground/50 text-muted-foreground text-xs transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('scorecard:scores.clear')}
          title={t('scorecard:scores.clear')}
        >
          <X className="h-3 w-3 sm:h-4 sm:w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
